<template>
  <ResizablePanels :show-right="isPreviewOpen && !isMobile">
    <template #panel-1>
      <Page.Header :crumbs="props.breadcrumbs">
        <Page.HeaderAction
          v-if="!props.playground && !isMobile"
          label="Preview"
          hotkey="$mod+p"
          variant="secondary"
          :active="isPreviewOpen"
          @click="showingPreviewState = !showingPreviewState"
        />
        <Menu.Root id="draft-page-actions">
          <Menu.Trigger>
            <Tooltip.Hint text="Actions">
              <button
                type="button"
                aria-label="Actions"
                data-emphasis-identifier="draft-page-actions-button"
                class="shrink-0 rounded-full border border-transparent p-[0.4375rem] text-gray-700 outline-2 outline-blue-500 transition hover:bg-gray-200 focus-visible:outline aria-expanded:bg-gray-200 sm:px-1.5 sm:py-1 dark:text-muted-foreground dark:hover:bg-card dark:aria-expanded:bg-card"
              >
                <EllipsisIcon class="size-4 sm:h-5" />
              </button>
            </Tooltip.Hint>
          </Menu.Trigger>

          <Menu.Container>
            <Menu.Item
              v-if="!props.playground && isMobile"
              label="Preview"
              icon="eye"
              hotkey="$mod+p"
              @click="showingPreviewState = !showingPreviewState"
            />

            <Menu.RouteItem
              v-if="props.draftTo"
              label="Send draft"
              icon="paper-airplane"
              :to="props.draftTo"
            />

            <Menu.RouteItem
              v-if="props.historyTo"
              label="History"
              icon="clock"
              :to="props.historyTo"
            />

            <EditorModeMenuSubmenu :mode="mode" @switch="switchMode" />

            <template v-if="props.docsUrl">
              <Menu.Divider />

              <Menu.LinkItem
                label="Documentation"
                icon="book-open"
                :href="props.docsUrl"
              />
            </template>
          </Menu.Container>
        </Menu.Root>
      </Page.Header>

      <ToNakedModeConfirmation
        v-if="modeSwitch.isSwitchingToNakedMode.value"
        :email="{
          subject: title || '',
          body: body || '',
          template: template || undefined,
        }"
        @close="modeSwitch.closeNakedModeConfirmation"
        @submit="modeSwitch.handleSwitchToNakedMode"
      />

      <EditorModeConfirmation
        v-if="modeSwitch.modeTransition.value"
        :transition="modeSwitch.modeTransition.value"
        @close="modeSwitch.closeModeTransitionConfirmation"
        @switch="modeSwitch.handleModeTransition"
      />

      <div class="empty:hidden">
        <FancyParseWarningCallout
          v-if="showFancyParseWarning"
          @close="showFancyParseWarning = false"
          @switch="mode = 'plaintext'"
        />
        <div
          v-if="props.warning"
          class="flex flex-col gap-2 border-y border-y-yellow-300 bg-yellow-100 px-4 py-3 text-yellow-600 @container first:border-t-0 @lg:flex-row @lg:items-center @lg:py-2"
        >
          <span class="grow text-sm">{{ props.warning }}</span>
        </div>
      </div>

      <div
        ref="scrollRef"
        class="relative flex h-full min-h-0 flex-1 basis-0 flex-col overflow-y-auto pt-2"
      >
        <TitleInput
          v-if="title !== undefined"
          v-model="title"
          class="text-2xl"
          style="padding-inline: max(20px, 50% - 650px / 2)"
          @focus-editor="activeEditor?.focus()"
        />

        <!-- Suspense is keyed so we'll show the fallback on mode changes -->
        <Suspense :key="mode">
          <TiptapEditor
            v-if="mode === 'fancy'"
            ref="activeEditor"
            v-model:body="body"
            :extensions="extensions"
            :template-tags="templateTags"
            @parse-error="showFancyParseWarning = true"
          />
          <PlainEditor
            v-else-if="mode === 'plaintext'"
            ref="activeEditor"
            v-model:body="body"
          />
          <NakedEditor
            v-else-if="mode === 'naked'"
            ref="activeEditor"
            v-model:body="body"
          />

          <template #fallback>
            <EditorSkeleton />
          </template>
        </Suspense>

        <Tray.Save
          :dirty="hasUnsavedChanges"
          @save="saveChanges"
          @reset="resetChanges"
        />
      </div>

      <RouterView />
    </template>

    <template #panel-2>
      <PreviewPane
        :source="source"
        :wrap-plaintext="wrapPlaintext"
        :rewrite-static-to-prod="!isEmailMedium"
        :show-toolbar="showSubscriberSelector"
      >
        <template #toolbar-end>
          <SubscriberPicker v-model="selectedSubscriberId" />
        </template>
      </PreviewPane>
    </template>
  </ResizablePanels>

  <Drawer.Root
    v-if="isPreviewOpen && isMobile"
    title="Preview"
    @close="showingPreviewState = false"
  >
    <PreviewPane
      :source="source"
      :wrap-plaintext="wrapPlaintext"
      :rewrite-static-to-prod="!isEmailMedium"
      :show-toolbar="showSubscriberSelector"
    >
      <template #toolbar-end>
        <SubscriberPicker v-model="selectedSubscriberId" />
      </template>
    </PreviewPane>
  </Drawer.Root>
</template>

<script lang="ts" setup>
import { computed, ref, useTemplateRef, watch } from "vue";

import {
  NakedEditor,
  PlainEditor,
  TiptapEditor,
} from "@/components/editor/async-components";
import TitleInput from "@/components/editor/components/TitleInput.vue";
import EditorModeMenuSubmenu from "@/components/editor/EditorModeMenuSubmenu.vue";
import EditorSkeleton from "@/components/editor/EditorSkeleton.vue";
import FancyParseWarningCallout from "@/components/editor/FancyParseWarningCallout.vue";
import ResizablePanels from "@/components/layout/ResizablePanels.vue";
import EditorModeConfirmation from "@/components/modals/EditorModeConfirmation.vue";
import ToNakedModeConfirmation from "@/components/modals/ToNakedModeConfirmation.vue";
import PreviewPane from "@/components/preview/PreviewPane.vue";
import SubscriberPicker from "@/components/preview/SubscriberPicker.vue";
import Drawer from "@/design_system/Drawer";
import Menu from "@/design_system/Menu";
import Page from "@/design_system/Page";
import type { BreadcrumbPath } from "@/design_system/Page/Header.vue";
import Tooltip from "@/design_system/Tooltip";
import Tray from "@/design_system/Tray";
import CodeBracketIcon from "@/icons/heroicons/code-bracket-micro.svg";
import EllipsisIcon from "@/icons/heroicons/ellipsis-horizontal-micro.svg";
import SparklesIcon from "@/icons/heroicons/sparkles-micro.svg";
import { useEmailDraftPreview } from "@/lib/api/queries/preview/email-draft";
import { useFeedDraftPreview } from "@/lib/api/queries/preview/feed-draft";
import { useNewsletterDraftPreview } from "@/lib/api/queries/preview/newsletter-draft";
import { useSubscribePagePreview } from "@/lib/api/queries/preview/subscribe-page";
import { useTransactionalDraftPreview } from "@/lib/api/queries/preview/transactional-draft";
import type {
  NewsletterDraftMedium,
  TransactionalEmail,
} from "@/lib/api/queries/preview/types";
import { useEditorModeState } from "@/lib/composables/editor-mode-state";
import { useEditorModeSwitch } from "@/lib/composables/editor-mode-switch";
import { useMediaQuery } from "@/lib/composables/media-query";
import { useShowingPreview } from "@/lib/composables/use-showing-preview";
import { clone } from "@/lib/dirty";
import { looksLikeHtml } from "@/lib/html-detection";
import { showOkapiErrorToast } from "@/lib/openapi-errors";
import type { RouteLocation } from "@/router/types";
import { useFeaturePermitted } from "@/store/stores/account";
import {
  useContextualSearchRoutes,
  useStore as useUIStatesStore,
} from "@/store/stores/ui-states";
import type { NewsletterEmailTemplate } from "@/types/email";
import type { Newsletter } from "@/types/newsletter";

const activeEditor = useTemplateRef("activeEditor");
const uiStates = useUIStatesStore();

const canUseNakedMode = useFeaturePermitted("naked_mode");

export type PreviewConfig =
  | { type: "email_draft" }
  | { type: "transactional_draft"; kind: TransactionalEmail }
  | { type: "feed_draft"; id: string }
  | { type: "newsletter_draft"; context: NewsletterDraftMedium }
  | { type: "subscribe_page" };

const props = defineProps<{
  breadcrumbs: BreadcrumbPath;
  previewConfig: PreviewConfig;
  save: () => Promise<void>;
  newsletter: Newsletter;
  draftTo?: RouteLocation;
  historyTo?: RouteLocation;
  docsUrl?: string;
  extensions?: string[];
  templateTags?: string[];
  warning?: string;
  playground?: boolean;
  showSubscriberSelector?: boolean;
}>();

const selectedSubscriberId = ref<string | undefined>(undefined);

useContextualSearchRoutes({
  "/__special_actions/fancy-mode": {
    title: "Switch to fancy mode",
    icon: SparklesIcon,
    action: () => {
      mode.value = "fancy";
    },
  },
  "/__special_actions/markdown-mode": {
    title: "Switch to markdown mode",
    icon: CodeBracketIcon,
    action: () => {
      mode.value = "plaintext";
    },
  },
});

const body = defineModel<string>({ required: true });
const title = defineModel<string>("title");
const template = defineModel<NewsletterEmailTemplate | null | undefined>(
  "template"
);

const initialBody = ref(body.value);
const initialTitle = ref(title.value);

const mode = useEditorModeState({
  defaultMode: () => {
    if (template.value === "naked") {
      return "naked";
    }
    if (canUseNakedMode.value && body.value && looksLikeHtml(body.value)) {
      if (!template.value) {
        template.value = "naked";
      }
      return "naked";
    }
    return uiStates.editorMode;
  },
});

const isMobile = useMediaQuery("(width < 768px)");

const showingPreviewState = useShowingPreview();
const isPreviewOpen = computed(
  () => props.playground || showingPreviewState.value
);

const showFancyParseWarning = ref(false);

watch(mode, (next) => {
  if (next !== "fancy") {
    showFancyParseWarning.value = false;
  }
});

const saveChanges = async () => {
  try {
    await props.save();

    initialTitle.value = title.value;
    initialBody.value = body.value;
  } catch (err) {
    showOkapiErrorToast(
      err,
      "Failed to save changes",
      "Please try again later."
    );
  }
};

const hasUnsavedChanges = computed(() => {
  if (props.playground) {
    return false;
  }
  return title.value !== initialTitle.value || body.value !== initialBody.value;
});

const resetChanges = () => {
  body.value = initialBody.value;
  title.value = initialTitle.value;
};

const { source, isEmailMedium, supportsSubscriber } = (() => {
  const config = props.previewConfig;

  switch (config.type) {
    case "email_draft": {
      return {
        source: useEmailDraftPreview(
          () => ({
            id: "preview",
            subject: title.value || "",
            body: body.value,
            template: template.value,
          }),
          {
            context: "email",
            enabled: isPreviewOpen,
            subscriberId: selectedSubscriberId,
          }
        ),
        isEmailMedium: true,
        supportsSubscriber: true,
      };
    }
    case "transactional_draft": {
      return {
        source: useTransactionalDraftPreview(
          () => ({
            kind: config.kind,
            subject: title.value || "",
            body: body.value,
            template: template.value,
          }),
          {
            enabled: isPreviewOpen,
            subscriberId: selectedSubscriberId,
          }
        ),
        isEmailMedium: true,
        supportsSubscriber: true,
      };
    }
    case "feed_draft": {
      return {
        source: useFeedDraftPreview(
          () => ({
            feedId: config.id,
            subject: title.value || "",
            body: body.value,
          }),
          {
            enabled: isPreviewOpen,
            subscriberId: selectedSubscriberId,
          }
        ),
        isEmailMedium: true,
        supportsSubscriber: true,
      };
    }
    case "newsletter_draft": {
      const ctx = config.context;
      return {
        source: useNewsletterDraftPreview(
          () => ({
            newsletter: clone(props.newsletter),
          }),
          {
            context: ctx,
            enabled: isPreviewOpen,
            subscriberId: selectedSubscriberId,
          }
        ),
        isEmailMedium: ctx === "email",
        supportsSubscriber: true,
      };
    }
    case "subscribe_page": {
      return {
        source: useSubscribePagePreview(
          () => ({
            newsletter: clone(props.newsletter),
          }),
          {
            enabled: isPreviewOpen,
          }
        ),
        isEmailMedium: false,
        supportsSubscriber: false,
      };
    }
  }
})();

const showSubscriberSelector = computed(() => {
  return supportsSubscriber && !!props.showSubscriberSelector;
});

const wrapPlaintext = computed(() => {
  return isEmailMedium && props.newsletter.only_send_plaintext_emails;
});

const modeSwitch = useEditorModeSwitch({
  mode,
  body,
  onModeChange: (next) => {
    mode.value = next;
  },
  onSwitchToNakedMode: (incomingBody) => {
    mode.value = "naked";
    template.value = "naked";
    body.value = incomingBody;
  },
  onClearBodyForModeTransition: () => {
    template.value = "plaintext";
    body.value = "";
  },
});

const switchMode = modeSwitch.switchMode;
</script>
